import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Configure axios
axios.defaults.withCredentials = true;

// API helper with error handling
const api = {
  // Generic request handler
  async request(method, endpoint, data = null, params = null) {
    try {
      const config = { method, url: `${API}/api${endpoint}`, params };
      if (data) config.data = data;
      const response = await axios(config);
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || error.message;
    }
  },

  // Auth
  async getMe() { return this.request('GET', '/auth/me'); },

  // Dashboard
  async getDashboardStats() { return this.request('GET', '/dashboard/stats'); },
  async getActivities(limit = 50) { return this.request('GET', '/activities', null, { limit }); },

  // Experiments
  async getExperiments(params = {}) { return this.request('GET', '/experiments', null, params); },
  async getExperiment(id) { return this.request('GET', `/experiments/${id}`); },
  async createExperiment(data) { return this.request('POST', '/experiments', data); },
  async updateExperiment(id, data) { return this.request('PUT', `/experiments/${id}`, data); },
  async deleteExperiment(id) { return this.request('DELETE', `/experiments/${id}`); },
  async cloneExperiment(id) { return this.request('POST', `/experiments/${id}/clone`); },

  // Protocols
  async getProtocols(params = {}) { return this.request('GET', '/protocols', null, params); },
  async getProtocol(id) { return this.request('GET', `/protocols/${id}`); },
  async createProtocol(data) { return this.request('POST', '/protocols', data); },
  async updateProtocol(id, data) { return this.request('PUT', `/protocols/${id}`, data); },
  async deleteProtocol(id) { return this.request('DELETE', `/protocols/${id}`); },

  // Plasmids
  async getPlasmids(params = {}) { return this.request('GET', '/plasmids', null, params); },
  async getPlasmid(id) { return this.request('GET', `/plasmids/${id}`); },
  async createPlasmid(data) { return this.request('POST', '/plasmids', data); },
  async updatePlasmid(id, data) { return this.request('PUT', `/plasmids/${id}`, data); },
  async deletePlasmid(id) { return this.request('DELETE', `/plasmids/${id}`); },
  async getPlasmidLineage(id) { return this.request('GET', `/plasmids/${id}/lineage`); },

  // Strains
  async getStrains(params = {}) { return this.request('GET', '/strains', null, params); },
  async getStrain(id) { return this.request('GET', `/strains/${id}`); },
  async createStrain(data) { return this.request('POST', '/strains', data); },
  async updateStrain(id, data) { return this.request('PUT', `/strains/${id}`, data); },
  async deleteStrain(id) { return this.request('DELETE', `/strains/${id}`); },
  async getStrainLineage(id) { return this.request('GET', `/strains/${id}/lineage`); },

  // Samples
  async getSamples(params = {}) { return this.request('GET', '/samples', null, params); },
  async getSample(id) { return this.request('GET', `/samples/${id}`); },
  async createSample(data) { return this.request('POST', '/samples', data); },
  async updateSample(id, data) { return this.request('PUT', `/samples/${id}`, data); },
  async deleteSample(id) { return this.request('DELETE', `/samples/${id}`); },

  // Inventory
  async getInventory(params = {}) { return this.request('GET', '/inventory', null, params); },
  async getInventoryItem(id) { return this.request('GET', `/inventory/${id}`); },
  async createInventoryItem(data) { return this.request('POST', '/inventory', data); },
  async updateInventoryItem(id, data) { return this.request('PUT', `/inventory/${id}`, data); },
  async deleteInventoryItem(id) { return this.request('DELETE', `/inventory/${id}`); },
  async useInventoryItem(id, amount, experimentId = null) {
    return this.request('POST', `/inventory/${id}/use`, null, { amount, experiment_id: experimentId });
  },
  async getInventoryAlerts() { return this.request('GET', '/inventory/alerts'); },

  // Inventory & Protocol Import
  async importInventory(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/api/inventory/import`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || error.message || 'Failed to process file';
    }
  },
  async confirmInventoryImport(items) { return this.request('POST', '/inventory/import/confirm', { items }); },
  async importProtocol(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/api/protocols/import`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || error.message || 'Failed to process file';
    }
  },
  async confirmProtocolImport(protocol) { return this.request('POST', '/protocols/import/confirm', { protocol }); },

  // Generic AI Import (experiments, samples, plasmids, strains, projects)
  async importGeneric(entityType, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/api/bulk-import/${entityType}`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || error.message || 'Failed to process file';
    }
  },
  async confirmGenericImport(entityType, items) { return this.request('POST', `/bulk-import/${entityType}/confirm`, { items }); },

  // File Attachments
  async uploadAttachment(entityType, entityId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/api/attachments/${entityType}/${entityId}`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
    return response.data;
  },
  async getAttachments(entityType, entityId) { return this.request('GET', `/attachments/${entityType}/${entityId}`); },
  async deleteAttachment(attachmentId) { return this.request('DELETE', `/attachments/${attachmentId}`); },

  // Protocol Execution
  async startProtocolRun(protoId, experimentId) { return this.request('POST', `/protocols/${protoId}/execute`, { experiment_id: experimentId }); },
  async getProtocolRuns(protoId) { return this.request('GET', `/protocols/${protoId}/runs`); },
  async getProtocolRun(runId) { return this.request('GET', `/protocol-runs/${runId}`); },
  async updateRunStep(runId, stepNum, action, data = {}) { return this.request('PUT', `/protocol-runs/${runId}/step/${stepNum}`, { action, ...data }); },
  async uploadStepPhoto(runId, stepNum, file, caption = '') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('caption', caption);
    const response = await axios.post(`${API}/api/protocol-runs/${runId}/photo/${stepNum}`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
    return response.data;
  },

  // Equipment & LN2
  async getEquipment(type) { return this.request('GET', `/equipment${type ? `?eq_type=${type}` : ''}`); },
  async getEquipmentDetail(id) { return this.request('GET', `/equipment/${id}`); },
  async createEquipment(data) { return this.request('POST', '/equipment', data); },
  async updateEquipment(id, data) { return this.request('PUT', `/equipment/${id}`, data); },
  async deleteEquipment(id) { return this.request('DELETE', `/equipment/${id}`); },
  async logEquipmentEvent(id, data) { return this.request('POST', `/equipment/${id}/log`, data); },

  // LN2 Cryostorage
  async initLN2Storage() { return this.request('POST', '/ln2-storage/init'); },
  async getLN2Towers() { return this.request('GET', '/ln2-storage'); },
  async getLN2Box(tower, box) { return this.request('GET', `/ln2-storage/${tower}/${box}`); },
  async updateLN2Slot(tower, box, slot, data) { return this.request('PUT', `/ln2-storage/${tower}/${box}/${slot}`, data); },

  // -80°C Freezer (Shelf → Rack → 4×4 boxes → 9×9 slots)
  async initFreezer80() { return this.request('POST', '/freezer80/init'); },
  async getFreezer80Overview() { return this.request('GET', '/freezer80'); },
  async getFreezer80Box(boxId) { return this.request('GET', `/freezer80/box/${boxId}`); },
  async updateFreezer80Slot(boxId, slotNum, data) { return this.request('PUT', `/freezer80/box/${boxId}/slot/${slotNum}`, data); },
  async searchFreezer80(q) { return this.request('GET', '/freezer80/search', null, { q }); },

  // Unified Bio-Inventory
  async bioInventoryOverview() { return this.request('GET', '/bio-inventory/overview'); },
  async addBioSample(data) { return this.request('POST', '/bio-inventory/samples', data); },
  async getBioSamples(params = {}) { return this.request('GET', '/bio-inventory/samples', null, params); },
  async getBioSample(id) { return this.request('GET', `/bio-inventory/samples/${id}`); },
  async moveBioSample(id, data) { return this.request('PUT', `/bio-inventory/samples/${id}/move`, data); },
  async removeBioSample(id) { return this.request('DELETE', `/bio-inventory/samples/${id}`); },
  async getBioMovements(params = {}) { return this.request('GET', '/bio-inventory/movements', null, params); },
  async getAvailableLocations(storageType) { return this.request('GET', '/bio-inventory/locations/available', null, { storage_type: storageType }); },

  // Sequences
  async getSequences(params = {}) { return this.request('GET', '/sequences', null, params); },
  async getSequence(id) { return this.request('GET', `/sequences/${id}`); },
  async createSequence(data) { return this.request('POST', '/sequences', data); },
  async deleteSequence(id) { return this.request('DELETE', `/sequences/${id}`); },

  // Datasets
  async getDatasets(params = {}) { return this.request('GET', '/datasets', null, params); },
  async getDataset(id) { return this.request('GET', `/datasets/${id}`); },
  async createDataset(data) { return this.request('POST', '/datasets', data); },
  async deleteDataset(id) { return this.request('DELETE', `/datasets/${id}`); },

  // Figures
  async getFigures(params = {}) { return this.request('GET', '/figures', null, params); },
  async getFigure(id) { return this.request('GET', `/figures/${id}`); },
  async createFigure(data) { return this.request('POST', '/figures', data); },
  async updateFigure(id, data) { return this.request('PUT', `/figures/${id}`, data); },
  async deleteFigure(id) { return this.request('DELETE', `/figures/${id}`); },

  // Sequence Tools
  async designPrimers(sequence, primerLength = 20, targetTm = 60) {
    return this.request('POST', '/tools/primer-design', { sequence, primer_length: primerLength, target_tm: targetTm });
  },
  async analyzeSequence(sequence) {
    return this.request('POST', '/tools/sequence-analysis', null, { sequence });
  },
  async optimizeCodons(sequence, organism = 'E. coli') {
    return this.request('POST', '/tools/codon-optimize', { sequence, organism });
  },
  async analyzeGrowthCurve(timePoints, odValues) {
    return this.request('POST', '/tools/growth-curve', { time_points: timePoints, od_values: odValues });
  },

  // AI Assistant
  async aiAssist(prompt, contextType = 'general', contextData = null) {
    return this.request('POST', '/ai/assist', { prompt, context_type: contextType, context_data: contextData });
  },
  async aiLabAssistant(question, sessionId) {
    return this.request('POST', '/ai/assistant', { question, session_id: sessionId });
  },
  async getAiChatHistory(sessionId) {
    return this.request('GET', '/ai/chat-history', null, sessionId ? { session_id: sessionId } : {});
  },
  async paperToProtocol(text, title) {
    return this.request('POST', '/ai/paper-to-protocol', { text, title });
  },

  // Failure Logging
  async createFailureLog(data) { return this.request('POST', '/failures', data); },
  async getFailureLogs(category, status) { return this.request('GET', '/failures', null, { category, status }); },
  async updateFailureLog(id, data) { return this.request('PUT', `/failures/${id}`, data); },
  async getFailurePatterns() { return this.request('GET', '/failures/patterns'); },

  // Experiment Reproducibility
  async saveExperimentMetadata(expId, metadata) { return this.request('POST', `/experiments/${expId}/metadata`, metadata); },
  async getExperimentMetadata(expId) { return this.request('GET', `/experiments/${expId}/metadata`); },
  async compareExperiments(expIds) { return this.request('POST', '/experiments/compare', expIds); },

  // Smart Inventory
  async getInventoryPredictions() { return this.request('GET', '/inventory/predictions'); },
  async getReorderList() { return this.request('GET', '/inventory/reorder-list'); },

  // Lineage Graph
  async createLineageLink(data) { return this.request('POST', '/lineage/links', data); },
  async getLineageGraph(entityType, entityId) { return this.request('GET', '/lineage/graph', null, { entity_type: entityType, entity_id: entityId }); },
  async deleteLineageLink(id) { return this.request('DELETE', `/lineage/links/${id}`); },

  // CRISPR gRNA Designer
  async designGrna(targetSequence, pam, guideLength, organism) {
    return this.request('POST', '/tools/crispr-design', { target_sequence: targetSequence, pam, guide_length: guideLength, organism });
  },

  // Protocol Runs (Bench Mode) - using new implementation at lines 126-136
  // Old duplicate methods removed to avoid conflicts

  // Projects
  async getProjects(status) { return this.request('GET', '/projects', null, status ? { status } : {}); },
  async getProject(id) { return this.request('GET', `/projects/${id}`); },
  async createProject(data) { return this.request('POST', '/projects', data); },
  async updateProject(id, data) { return this.request('PUT', `/projects/${id}`, data); },
  async deleteProject(id) { return this.request('DELETE', `/projects/${id}`); },
  async addBenchmark(projectId, data) { return this.request('POST', `/projects/${projectId}/benchmarks`, data); },
  async updateBenchmark(projectId, benchmarkId, data) { return this.request('PUT', `/projects/${projectId}/benchmarks/${benchmarkId}`, data); },
  async createTask(projectId, data) { return this.request('POST', `/projects/${projectId}/tasks`, data); },
  async updateTask(projectId, taskId, data) { return this.request('PUT', `/projects/${projectId}/tasks/${taskId}`, data); },
  async deleteTask(projectId, taskId) { return this.request('DELETE', `/projects/${projectId}/tasks/${taskId}`); },
  async addSubfolder(projectId, data) { return this.request('POST', `/projects/${projectId}/subfolders`, data); },
  async removeSubfolder(projectId, folderId) { return this.request('DELETE', `/projects/${projectId}/subfolders/${folderId}`); },
  async linkEntity(projectId, data) { return this.request('POST', `/projects/${projectId}/link`, data); },
  async unlinkEntity(projectId, linkId) { return this.request('DELETE', `/projects/${projectId}/link/${linkId}`); },

  // Slack Integration
  async getSlackStatus() { return this.request('GET', '/slack/status'); },
  async getSlackUsers() { return this.request('GET', '/slack/users'); },
  async syncSlackUsers() { return this.request('POST', '/slack/sync-users'); },
  async getSlackUserMap() { return this.request('GET', '/slack/user-map'); },
  async linkSlackChannel(labChannelId, slackChannelId, slackChannelName) {
    return this.request('POST', '/slack/link-channel', { lab_channel_id: labChannelId, slack_channel_id: slackChannelId, slack_channel_name: slackChannelName });
  },
  async unlinkSlackChannel(labChannelId) { return this.request('DELETE', `/slack/link-channel/${labChannelId}`); },
  async getSlackChannelLinks() { return this.request('GET', '/slack/channel-links'); },
  async pullSlackMessages(labChannelId) { return this.request('POST', `/slack/pull-messages/${labChannelId}`); },

  // Search
  async search(query, entityType = null) {
    return this.request('GET', '/search', null, { q: query, entity_type: entityType });
  },

  // Folders
  async getFolders(parentId = null) { return this.request('GET', '/folders', null, { parent_id: parentId }); },
  async getFolder(id) { return this.request('GET', `/folders/${id}`); },
  async createFolder(name, parentId = null) { return this.request('POST', '/folders', { name, parent_id: parentId }); },
  async updateFolder(id, name) { return this.request('PUT', `/folders/${id}`, { name }); },
  async deleteFolder(id) { return this.request('DELETE', `/folders/${id}`); },
  async uploadFileToFolder(folderId, file) {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await import('axios').then(m => m.default.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/folders/${folderId}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
      ));
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || error.message;
    }
  },
  async linkEntityToFolder(folderId, entityType, entityId, entityName) {
    return this.request('POST', `/folders/${folderId}/link`, null, { entity_type: entityType, entity_id: entityId, entity_name: entityName });
  },
  async bookmarkPaperToFolder(folderId, paper) {
    return this.request('POST', `/folders/${folderId}/bookmark`, paper);
  },
  async deleteFolderItem(itemId) { return this.request('DELETE', `/folder-items/${itemId}`); },
  async moveFolderItem(itemId, targetFolderId) { return this.request('PUT', `/folder-items/${itemId}/move`, null, { target_folder_id: targetFolderId }); },

  // Research
  async searchPapers(query, offset = 0, limit = 20, year = null) {
    const params = { q: query, offset, limit };
    if (year) params.year = year;
    return this.request('GET', '/research/search', null, params);
  },
  async getPaperDetails(paperId) { return this.request('GET', `/research/paper/${paperId}`); },
  async getBookmarks() { return this.request('GET', '/research/bookmarks'); },

  // Chat
  async getChatChannels() { return this.request('GET', '/chat/channels'); },
  async createChatChannel(name, description = '', type = 'public', memberIds = []) {
    return this.request('POST', '/chat/channels', { name, description, type, member_ids: memberIds });
  },
  async getOrCreateDM(memberId) { return this.request('POST', '/chat/dm', null, { member_id: memberId }); },
  async getChatChannel(id) { return this.request('GET', `/chat/channels/${id}`); },
  async getChatMessages(channelId, before = null, limit = 50) {
    const params = { limit };
    if (before) params.before = before;
    return this.request('GET', `/chat/channels/${channelId}/messages`, null, params);
  },
  async sendChatMessage(channelId, content, threadId = null, mentions = []) {
    return this.request('POST', `/chat/channels/${channelId}/messages`, { content, thread_id: threadId, mentions });
  },
  async uploadChatFile(channelId, file, threadId = null) {
    const formData = new FormData();
    formData.append('file', file);
    const params = threadId ? `?thread_id=${threadId}` : '';
    try {
      const response = await import('axios').then(m => m.default.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/chat/channels/${channelId}/upload${params}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
      ));
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || error.message;
    }
  },
  async getThread(threadId) { return this.request('GET', `/chat/threads/${threadId}`); },
  async editMessage(messageId, content) { return this.request('PUT', `/chat/messages/${messageId}`, null, { content }); },
  async deleteMessage(messageId) { return this.request('DELETE', `/chat/messages/${messageId}`); },
  async togglePin(messageId) { return this.request('POST', `/chat/messages/${messageId}/pin`); },
  async reactToMessage(messageId, emoji) { return this.request('POST', `/chat/messages/${messageId}/react`, null, { emoji }); },
  async searchChat(query, channelId = null) {
    const params = { q: query };
    if (channelId) params.channel_id = channelId;
    return this.request('GET', '/chat/search', null, params);
  },
  async getChatUsers() { return this.request('GET', '/chat/users'); },

  // Calendly
  async getCalendlyUser() { return this.request('GET', '/calendly/user'); },
  async getCalendlyEventTypes() { return this.request('GET', '/calendly/event-types'); },
  async getCalendlyEvents(minDate, maxDate, status = 'active') {
    const params = {};
    if (minDate) params.min_date = minDate;
    if (maxDate) params.max_date = maxDate;
    if (status) params.status = status;
    return this.request('GET', '/calendly/events', null, params);
  },
  async getCalendlySchedulingLink() { return this.request('GET', '/calendly/scheduling-link'); }
};

export default api;
